import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { OrderListBreadcrumbs } from '@components/OrderProcessing/OrderListBreadcrumbs';
import { ButcherHeader } from '@components/ButcherHeader';
import {
  DashboardCrumbSteps,
  errorMessage,
  OrderProcessingMasterData,
} from '@helpers/Constants';
import DatePicker from 'react-datepicker';
import { Axios } from '@helpers/ApiService';
import { useToast } from '@contexts/Toast';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import { DashboardData } from '@helpers/Models';
import { format } from 'date-fns';

export const Dashboard: React.FC = () => {
  const { showToastMessage } = useToast();
  const [loading, setLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<Period>('Last 30 Days');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const PeriodOptions: Period[] = [
    'Last 30 Days',
    'Last 60 Days',
    'Last Quarter',
    'Current Year',
  ];
  type Period =
    | 'Last 30 Days'
    | 'Last 60 Days'
    | 'Last Quarter'
    | 'Current Year';

  const calendarRef = useRef<HTMLDivElement>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [data, setData] = useState<DashboardData | null>(null);
  // const [isSubscriptionActive, setIsSubscriptionActive] =
  //   useState<boolean>(false);

  const BreadCrumbsProps = {
    steps: DashboardCrumbSteps,
    activeItem: 'Dashboard',
    type: OrderProcessingMasterData.DASHBOARD,
  };

  // Close calendar and dropdown when clicking outside
  // useEffect(() => {
  //   // Verify the subscription status
  //   setIsSubscriptionActive(SubscriptionValidation());

  //   const handleClickOutside = (event: MouseEvent) => {
  //     if (
  //       calendarRef.current &&
  //       !calendarRef.current.contains(event.target as Node)
  //     ) {
  //       setCalendarOpen(false);
  //     }
  //     if (
  //       dropdownRef.current &&
  //       !dropdownRef.current.contains(event.target as Node)
  //     ) {
  //       setIsDropdownOpen(false);
  //     }
  //   };
  //   document.addEventListener('mousedown', handleClickOutside);
  //   return () => document.removeEventListener('mousedown', handleClickOutside);
  // }, []);

  const fmt = (d: Date) => format(d, 'yyyy-MM-dd');

  /**
   * Returns a date range based on the selected period.
   *
   * Supports predefined periods like "Last 30 Days", "Last 60 Days",
   * previous quarter, and current year.
   *
   * @param {Period} period - The selected time range
   * @returns {{ fromDate: string; toDate: string }} Formatted date range
   */
  const getDateRange = (
    period: Period,
  ): { fromDate: string; toDate: string } => {
    const today = new Date();
    if (period === 'Last 30 Days') {
      const from = new Date(today);
      from.setDate(from.getDate() - 30);
      return { fromDate: fmt(from), toDate: fmt(today) };
    }
    if (period === 'Last 60 Days') {
      const from = new Date(today);
      from.setDate(from.getDate() - 60);
      return { fromDate: fmt(from), toDate: fmt(today) };
    }
    if (period === 'Last Quarter') {
      const month = today.getMonth();
      const year = today.getFullYear();
      const currentQ = Math.floor(month / 3);
      const lastQStartMonth = currentQ === 0 ? 9 : (currentQ - 1) * 3;
      const lastQYear = currentQ === 0 ? year - 1 : year;
      const from = new Date(lastQYear, lastQStartMonth, 1);
      const to = new Date(lastQYear, lastQStartMonth + 3, 0);
      return { fromDate: fmt(from), toDate: fmt(to) };
    }
    if (period === 'Current Year') {
      return { fromDate: `${today.getFullYear()}-01-01`, toDate: fmt(today) };
    }
    return { fromDate: fmt(today), toDate: fmt(today) };
  };

  /**
   * Fetches dashboard data for a given date range.
   *
   * - Sends a request with `fromDate` and `toDate` to retrieve dashboard metrics
   * - Updates the dashboard state with the response data
   * - Manages loading state during the API call
   * - Displays an error toast if the request fails
   *
   * @param {string} fromDate - Start date of the range (formatted string)
   * @param {string} toDate - End date of the range (formatted string)
   * @returns {Promise<void>} Resolves when the fetch operation completes
   */
  const fetchDashboard = useCallback(
    async (fromDate: string, toDate: string) => {
      try {
        setLoading(true);

        const response = await Axios.post(ApiEndpoints.FETCH_DASHBOARD, {
          fromDate,
          toDate,
        });

        setData(response.data.data);
      } catch (error) {
        showToastMessage(errorMessage.FETCH_ERROR, 'error');
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  // Fetch dashboard data when selected period changes
  useEffect(() => {
    const { fromDate, toDate } = getDateRange(selectedPeriod);
    fetchDashboard(fromDate, toDate);
  }, [selectedPeriod, fetchDashboard]);

  return (
    <div className="flex h-screen w-screen">
      <SideNav active={1} />
      {/* Main Content */}
      <div
        className={`flex-1 p-2 overflow-auto relative ${loading ? 'opacity-50' : 'opacity-100'}`}
      >
        {/* Header */}
        <ButcherHeader />
        <OrderListBreadcrumbs orderInfo={BreadCrumbsProps} />
        {/* Do not remove the following code as we need clarifications on how we handling create new order */}
        {/* <div className="p-2 flex items-center justify-between mt-2">
          <h1 className="text-2xl tracking-[4%] font-bold text-[#1A202C]">
            Dashboard
          </h1>
          <button
            type="button"
            disabled={isSubscriptionActive}
            className={`flex items-center gap-2 ${isSubscriptionActive ? 'cursor-not-allowed bg-[#00483480]' : 'bg-primary hover:bg-dark-primary'} text-white font-semibold rounded-md px-4 py-2.5`}
          >
            + Create New Order
          </button>
        </div> */}

        <div className="flex items-center justify-between mt-2">
          <h1 className="text-xl font-semibold text-[#1A1A1A]">
            Order & Customer Analytics
          </h1>
          <div className="flex gap-5">
            <div className="relative w-max mx-auto" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setIsDropdownOpen((prev) => !prev)}
                className={`px-5 py-2.5 rounded-sm cursor-pointer text-slate-900 text-sm font-medium outline-none bg-[#F9FAFB] hover:bg-gray-50 transition-colors ${
                  isDropdownOpen ? 'border-blue-400' : 'border-gray-300'
                }`}
              >
                Period: {selectedPeriod}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`w-3 fill-gray-500 inline ml-2 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                  viewBox="0 0 24 24"
                >
                  <path
                    fillRule="evenodd"
                    d="M11.99997 18.1669a2.38 2.38 0 0 1-1.68266-.69733l-9.52-9.52a2.38 2.38 0 1 1 3.36532-3.36532l7.83734 7.83734 7.83734-7.83734a2.38 2.38 0 1 1 3.36532 3.36532l-9.52 9.52a2.38 2.38 0 0 1-1.68266.69734z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {isDropdownOpen && (
                <ul className="absolute left-0 top-full mt-1 rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.10)] bg-white py-1 z-[1000] min-w-full w-max">
                  {PeriodOptions.map((option) => (
                    <li
                      key={option}
                      onClick={() => {
                        setSelectedPeriod(option);
                        setIsDropdownOpen(false);
                      }}
                      className={`px-5 py-3 text-sm cursor-pointer transition-colors ${
                        selectedPeriod === option
                          ? 'font-semibold'
                          : 'text-[#000A16] hover:bg-gray-100'
                      }`}
                    >
                      {option}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Date Range Selector */}
            <div className="relative" ref={calendarRef}>
              <button
                type="button"
                className={`w-10 h-10 flex items-center justify-center rounded-[2px] transition-colors bg-[#F9FAFB] hover:bg-gray-100
                  }`}
                onClick={() => setCalendarOpen((v) => !v)}
                title="Custom date range"
              >
                <svg
                  width="18"
                  height="20"
                  viewBox="0 0 18 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2 19.375C1.45 19.375 0.979167 19.1792 0.5875 18.7875C0.195833 18.3958 0 17.925 0 17.375V3.375C0 2.825 0.195833 2.35417 0.5875 1.9625C0.979167 1.57083 1.45 1.375 2 1.375H3V-0.625H5V1.375H13V-0.625H15V1.375H16C16.55 1.375 17.0208 1.57083 17.4125 1.9625C17.8042 2.35417 18 2.825 18 3.375V17.375C18 17.925 17.8042 18.3958 17.4125 18.7875C17.0208 19.1792 16.55 19.375 16 19.375H2ZM2 17.375H16V7.375H2V17.375ZM2 5.375H16V3.375H2V5.375ZM9 11.375C8.71667 11.375 8.47917 11.2792 8.2875 11.0875C8.09583 10.8958 8 10.6583 8 10.375C8 10.0917 8.09583 9.85417 8.2875 9.6625C8.47917 9.47083 8.71667 9.375 9 9.375C9.28333 9.375 9.52083 9.47083 9.7125 9.6625C9.90417 9.85417 10 10.0917 10 10.375C10 10.6583 9.90417 10.8958 9.7125 11.0875C9.52083 11.2792 9.28333 11.375 9 11.375ZM4.2875 11.0875C4.09583 10.8958 4 10.6583 4 10.375C4 10.0917 4.09583 9.85417 4.2875 9.6625C4.47917 9.47083 4.71667 9.375 5 9.375C5.28333 9.375 5.52083 9.47083 5.7125 9.6625C5.90417 9.85417 6 10.0917 6 10.375C6 10.6583 5.90417 10.8958 5.7125 11.0875C5.52083 11.2792 5.28333 11.375 5 11.375C4.71667 11.375 4.47917 11.2792 4.2875 11.0875ZM13 11.375C12.7167 11.375 12.4792 11.2792 12.2875 11.0875C12.0958 10.8958 12 10.6583 12 10.375C12 10.0917 12.0958 9.85417 12.2875 9.6625C12.4792 9.47083 12.7167 9.375 13 9.375C13.2833 9.375 13.5208 9.47083 13.7125 9.6625C13.9042 9.85417 14 10.0917 14 10.375C14 10.6583 13.9042 10.8958 13.7125 11.0875C13.5208 11.2792 13.2833 11.375 13 11.375ZM9 15.375C8.71667 15.375 8.47917 15.2792 8.2875 15.0875C8.09583 14.8958 8 14.6583 8 14.375C8 14.0917 8.09583 13.8542 8.2875 13.6625C8.47917 13.4708 8.71667 13.375 9 13.375C9.28333 13.375 9.52083 13.4708 9.7125 13.6625C9.90417 13.8542 10 14.0917 10 14.375C10 14.6583 9.90417 14.8958 9.7125 15.0875C9.52083 15.2792 9.28333 15.375 9 15.375ZM4.2875 15.0875C4.09583 14.8958 4 14.6583 4 14.375C4 14.0917 4.09583 13.8542 4.2875 13.6625C4.47917 13.4708 4.71667 13.375 5 13.375C5.28333 13.375 5.52083 13.4708 5.7125 13.6625C5.90417 13.8542 6 14.0917 6 14.375C6 14.6583 5.90417 14.8958 5.7125 15.0875C5.52083 15.2792 5.28333 15.375 5 15.375C4.71667 15.375 4.47917 15.2792 4.2875 15.0875ZM13 15.375C12.7167 15.375 12.4792 15.2792 12.2875 15.0875C12.0958 14.8958 12 14.6583 12 14.375C12 14.0917 12.0958 13.8542 12.2875 13.6625C12.4792 13.4708 12.7167 13.375 13 13.375C13.2833 13.375 13.5208 13.4708 13.7125 13.6625C13.9042 13.8542 14 14.0917 14 14.375C14 14.6583 13.9042 14.8958 13.7125 15.0875C13.5208 15.2792 13.2833 15.375 13 15.375Z"
                    fill="#585858"
                  />
                </svg>
              </button>

              {calendarOpen && (
                <div className="absolute right-0 z-30 mt-2">
                  <div className="dashboard-dp">
                    <DatePicker
                      selectsRange
                      startDate={dateRange[0] ?? undefined}
                      endDate={dateRange[1] ?? undefined}
                      onChange={(update: [Date | null, Date | null]) => {
                        setDateRange(update);
                        if (update[0] && update[1]) {
                          fetchDashboard(fmt(update[0]), fmt(update[1]));
                          setCalendarOpen(false);
                        }
                      }}
                      inline
                      formatWeekDay={(day) => day.substring(0, 2)}
                      renderCustomHeader={({
                        date,
                        decreaseMonth,
                        increaseMonth,
                      }) => (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '8px 12px 12px',
                          }}
                        >
                          <button
                            type="button"
                            onClick={decreaseMonth}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              color: '#6b7280',
                              fontSize: '16px',
                              lineHeight: 1,
                            }}
                          >
                            &#8249;
                          </button>
                          <span
                            style={{
                              fontFamily: 'Inter, sans-serif',
                              fontWeight: 700,
                              fontSize: '14px',
                              color: '#1a1a1a',
                            }}
                          >
                            {date.toLocaleString('default', {
                              month: 'long',
                              year: 'numeric',
                            })}
                          </span>
                          <button
                            type="button"
                            onClick={increaseMonth}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              color: '#6b7280',
                              fontSize: '16px',
                              lineHeight: 1,
                            }}
                          >
                            &#8250;
                          </button>
                        </div>
                      )}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* First three cards */}
        <div className="flex gap-3.5 mt-3 mb-3.5">
          <div className="flex-1 bg-white border border-gray-200 rounded-lg p-5 min-w-0">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-[#6C757D] uppercase tracking-widest mb-2">
                  TOTAL ORDERS
                </p>
                <p className="text-[28px] font-bold text-gray-900 leading-none mb-2">
                  {data?.orderCounts?.total?.toLocaleString() ?? '—'}
                </p>
              </div>
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#EDE9FE]`}
              >
                <svg
                  width="17"
                  height="17"
                  viewBox="0 0 17 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.31999 16.1771C2.9936 15.8507 2.8304 15.4583 2.8304 15C2.8304 14.5417 2.9936 14.1493 3.31999 13.8229C3.64638 13.4965 4.03874 13.3333 4.49707 13.3333C4.9554 13.3333 5.34776 13.4965 5.67415 13.8229C6.00054 14.1493 6.16374 14.5417 6.16374 15C6.16374 15.4583 6.00054 15.8507 5.67415 16.1771C5.34776 16.5035 4.9554 16.6667 4.49707 16.6667C4.03874 16.6667 3.64638 16.5035 3.31999 16.1771ZM11.6533 16.1771C11.3269 15.8507 11.1637 15.4583 11.1637 15C11.1637 14.5417 11.3269 14.1493 11.6533 13.8229C11.9797 13.4965 12.3721 13.3333 12.8304 13.3333C13.2887 13.3333 13.6811 13.4965 14.0075 13.8229C14.3339 14.1493 14.4971 14.5417 14.4971 15C14.4971 15.4583 14.3339 15.8507 14.0075 16.1771C13.6811 16.5035 13.2887 16.6667 12.8304 16.6667C12.3721 16.6667 11.9797 16.5035 11.6533 16.1771ZM3.78874 3.33333L5.78874 7.5H11.6221L13.9137 3.33333H3.78874ZM2.99707 1.66667H15.2887C15.6082 1.66667 15.8512 1.80903 16.0179 2.09375C16.1846 2.37847 16.1915 2.66667 16.0387 2.95833L13.0804 8.29167C12.9276 8.56944 12.7228 8.78472 12.4658 8.9375C12.2089 9.09028 11.9276 9.16667 11.6221 9.16667H5.41374L4.49707 10.8333H14.4971V12.5H4.49707C3.87207 12.5 3.39985 12.2257 3.0804 11.6771C2.76096 11.1285 2.74707 10.5833 3.03874 10.0417L4.16374 8L1.16374 1.66667H-0.50293V0H2.2054L2.99707 1.66667Z"
                    fill="#8B5CF6"
                  />
                </svg>
              </div>
            </div>
          </div>
          <div className="flex-1 bg-white border border-gray-200 rounded-lg p-5 min-w-0">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-[#6C757D] uppercase tracking-widest mb-2">
                  TOTAL REVENUE
                </p>
                <p className="text-[28px] font-bold text-gray-900 leading-none mb-2">
                  ${data?.butcherTotalRevenue?.toLocaleString() ?? '—'}
                </p>
              </div>
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#DBEAFE]`}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 15 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1.6 14.4C1.16 14.4 0.783333 14.2433 0.47 13.93C0.156667 13.6167 0 13.24 0 12.8V0H1.6V12.8H14.4V14.4H1.6ZM2.4 12V4.8H5.6V12H2.4ZM6.4 12V0.8H9.6V12H6.4ZM10.4 12V8H13.6V12H10.4Z"
                    fill="#3B82F6"
                  />
                </svg>
              </div>
            </div>
          </div>
          <div className="flex-1 bg-white border border-gray-200 rounded-lg p-5 min-w-0">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-[#6C757D] uppercase tracking-widest mb-2">
                  AVERAGE ORDER VALUE
                </p>
                <p className="text-[28px] font-bold text-gray-900 leading-none mb-2">
                  ${data?.averageOrderValue?.toLocaleString() ?? '—'}
                </p>
              </div>
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#D1FAE5]`}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.27902 13.598H8.67902V12.598C9.34569 12.478 9.91902 12.218 10.399 11.818C10.879 11.418 11.119 10.8247 11.119 10.038C11.119 9.47805 10.959 8.96471 10.639 8.49805C10.319 8.03138 9.67902 7.62471 8.71902 7.27805C7.91902 7.01138 7.36569 6.77805 7.05902 6.57805C6.75236 6.37805 6.59902 6.10471 6.59902 5.75805C6.59902 5.41138 6.72236 5.13805 6.96902 4.93805C7.21569 4.73805 7.57236 4.63805 8.03902 4.63805C8.46569 4.63805 8.79902 4.74138 9.03902 4.94805C9.27902 5.15471 9.45236 5.41138 9.55902 5.71805L10.839 5.19805C10.6924 4.73138 10.4224 4.32471 10.029 3.97805C9.63569 3.63138 9.19902 3.43805 8.71902 3.39805V2.39805H7.31902V3.39805C6.65236 3.54471 6.13236 3.83805 5.75902 4.27805C5.38569 4.71805 5.19902 5.21138 5.19902 5.75805C5.19902 6.38471 5.38236 6.89138 5.74902 7.27805C6.11569 7.66471 6.69236 7.99805 7.47902 8.27805C8.31902 8.58471 8.90236 8.85805 9.22902 9.09805C9.55569 9.33805 9.71902 9.65138 9.71902 10.038C9.71902 10.478 9.56236 10.8014 9.24902 11.008C8.93569 11.2147 8.55902 11.318 8.11902 11.318C7.67902 11.318 7.28902 11.1814 6.94902 10.908C6.60902 10.6347 6.35902 10.2247 6.19902 9.67805L4.87902 10.198C5.06569 10.838 5.35569 11.3547 5.74902 11.748C6.14236 12.1414 6.65236 12.4114 7.27902 12.558V13.598ZM7.99902 15.998C6.89236 15.998 5.85236 15.788 4.87902 15.368C3.90569 14.948 3.05902 14.378 2.33902 13.658C1.61902 12.938 1.04902 12.0914 0.629023 11.118C0.209023 10.1447 -0.000976562 9.10471 -0.000976562 7.99805C-0.000976562 6.89138 0.209023 5.85138 0.629023 4.87805C1.04902 3.90471 1.61902 3.05805 2.33902 2.33805C3.05902 1.61805 3.90569 1.04805 4.87902 0.628047C5.85236 0.208047 6.89236 -0.00195312 7.99902 -0.00195312C9.10569 -0.00195312 10.1457 0.208047 11.119 0.628047C12.0924 1.04805 12.939 1.61805 13.659 2.33805C14.379 3.05805 14.949 3.90471 15.369 4.87805C15.789 5.85138 15.999 6.89138 15.999 7.99805C15.999 9.10471 15.789 10.1447 15.369 11.118C14.949 12.0914 14.379 12.938 13.659 13.658C12.939 14.378 12.0924 14.948 11.119 15.368C10.1457 15.788 9.10569 15.998 7.99902 15.998ZM7.99902 14.398C9.78569 14.398 11.299 13.778 12.539 12.538C13.779 11.298 14.399 9.78471 14.399 7.99805C14.399 6.21138 13.779 4.69805 12.539 3.45805C11.299 2.21805 9.78569 1.59805 7.99902 1.59805C6.21236 1.59805 4.69902 2.21805 3.45902 3.45805C2.21902 4.69805 1.59902 6.21138 1.59902 7.99805C1.59902 9.78471 2.21902 11.298 3.45902 12.538C4.69902 13.778 6.21236 14.398 7.99902 14.398Z"
                    fill="#009362"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
        {/* Highest Orders by Species and Order Status Breakdown */}
        <div className="flex gap-3.5">
          <div className="flex-1 bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <span className="p-3 rounded-md bg-[#D1FAE5]">
                  <svg
                    width="18"
                    height="12"
                    viewBox="0 0 18 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M0 12V10H6V12H0ZM0 7V5H12V7H0ZM0 2V0H18V2H0Z"
                      fill="#009362"
                    />
                  </svg>
                </span>
                <span className="font-bold text-[#1A1A1A]">
                  Highest Orders by Species
                </span>
              </div>
              <span className="text-[#1A1A1A]">{selectedPeriod}</span>
            </div>
            <div className="flex flex-col gap-2">
              {data?.highestOrderSpecies?.length ? (
                data.highestOrderSpecies.map((species) => {
                  const avg =
                    species.totalOrders > 0
                      ? Math.round(species.totalAmount / species.totalOrders)
                      : 0;
                  return (
                    <div
                      key={species.speciesId}
                      className="flex items-center justify-between py-2.5 px-2 bg-[#F8F9FA] rounded"
                    >
                      <div>
                        <p className="text-sm font-semibold text-[#1A1A1A]">
                          {species.name}
                        </p>
                        <p className="text-[12px] font-medium text-[#6C757D]">
                          {species.totalOrders.toLocaleString()} orders
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-[15px] font-bold text-gray-900">
                          ${species.totalAmount.toLocaleString()}
                        </p>
                        <p className="text-[12px] font-medium text-[#6C757D]">
                          Avg: ${avg.toLocaleString()}/order
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">
                  No data available
                </p>
              )}
            </div>
          </div>

          {/* Order Status Breakdown */}
          <div className="flex-1 bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <span className="p-3 rounded-md bg-[#FEF3C7]">
                  <svg
                    width="18"
                    height="10"
                    viewBox="0 0 18 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 2V0H18V2H4ZM4 6V4H18V6H4ZM4 10V8H18V10H4ZM1 2C0.716667 2 0.479167 1.90417 0.2875 1.7125C0.0958333 1.52083 0 1.28333 0 1C0 0.716667 0.0958333 0.479167 0.2875 0.2875C0.479167 0.0958333 0.716667 0 1 0C1.28333 0 1.52083 0.0958333 1.7125 0.2875C1.90417 0.479167 2 0.716667 2 1C2 1.28333 1.90417 1.52083 1.7125 1.7125C1.52083 1.90417 1.28333 2 1 2ZM1 6C0.716667 6 0.479167 5.90417 0.2875 5.7125C0.0958333 5.52083 0 5.28333 0 5C0 4.71667 0.0958333 4.47917 0.2875 4.2875C0.479167 4.09583 0.716667 4 1 4C1.28333 4 1.52083 4.09583 1.7125 4.2875C1.90417 4.47917 2 4.71667 2 5C2 5.28333 1.90417 5.52083 1.7125 5.7125C1.52083 5.90417 1.28333 6 1 6ZM1 10C0.716667 10 0.479167 9.90417 0.2875 9.7125C0.0958333 9.52083 0 9.28333 0 9C0 8.71667 0.0958333 8.47917 0.2875 8.2875C0.479167 8.09583 0.716667 8 1 8C1.28333 8 1.52083 8.09583 1.7125 8.2875C1.90417 8.47917 2 8.71667 2 9C2 9.28333 1.90417 9.52083 1.7125 9.7125C1.52083 9.90417 1.28333 10 1 10Z"
                      fill="#D97706"
                    />
                  </svg>
                </span>
                <span className="font-bold text-[#1A1A1A]">
                  Order Status Breakdown
                </span>
              </div>
              <span className="text-[#1A1A1A]">{selectedPeriod}</span>
            </div>
            {/* Total Orders */}
            <div className="flex items-center justify-between py-2.5 bg-[#F8F9FA] mt-2 mb-2">
              <div className="flex items-center gap-2.5">
                <span>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4.12875 17.4713C3.77625 17.1188 3.6 16.695 3.6 16.2C3.6 15.705 3.77625 15.2813 4.12875 14.9288C4.48125 14.5763 4.905 14.4 5.4 14.4C5.895 14.4 6.31875 14.5763 6.67125 14.9288C7.02375 15.2813 7.2 15.705 7.2 16.2C7.2 16.695 7.02375 17.1188 6.67125 17.4713C6.31875 17.8238 5.895 18 5.4 18C4.905 18 4.48125 17.8238 4.12875 17.4713ZM13.1288 17.4713C12.7763 17.1188 12.6 16.695 12.6 16.2C12.6 15.705 12.7763 15.2813 13.1288 14.9288C13.4813 14.5763 13.905 14.4 14.4 14.4C14.895 14.4 15.3188 14.5763 15.6713 14.9288C16.0238 15.2813 16.2 15.705 16.2 16.2C16.2 16.695 16.0238 17.1188 15.6713 17.4713C15.3188 17.8238 14.895 18 14.4 18C13.905 18 13.4813 17.8238 13.1288 17.4713ZM4.635 3.6L6.795 8.1H13.095L15.57 3.6H4.635ZM3.78 1.8H17.055C17.4 1.8 17.6625 1.95375 17.8425 2.26125C18.0225 2.56875 18.03 2.88 17.865 3.195L14.67 8.955C14.505 9.255 14.2838 9.4875 14.0063 9.6525C13.7288 9.8175 13.425 9.9 13.095 9.9H6.39L5.4 11.7H16.2V13.5H5.4C4.725 13.5 4.215 13.2038 3.87 12.6113C3.525 12.0188 3.51 11.43 3.825 10.845L5.04 8.64L1.8 1.8H0V0H2.925L3.78 1.8Z"
                      fill="#3B82F6"
                    />
                  </svg>
                </span>
                <span className="text-[#374151] font-medium">Total Orders</span>
              </div>
              <span className="text-[16px] font-bold text-[#1A1A1A]">
                {data?.orderCounts?.total?.toLocaleString() ?? '—'}
              </span>
            </div>
            {/* Completed Orders  */}
            <div className="flex items-center justify-between py-2.5 bg-[#F8F9FA] mt-2 mb-2">
              <div className="flex items-center gap-2.5">
                <span>
                  <svg
                    width="19"
                    height="19"
                    viewBox="0 0 19 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.88333 13.3833L14.3458 6.92083L13.0625 5.6375L7.88333 10.8167L5.27083 8.20417L3.9875 9.4875L7.88333 13.3833ZM9.16667 18.3333C7.89861 18.3333 6.70694 18.0927 5.59167 17.6115C4.47639 17.1302 3.50625 16.4771 2.68125 15.6521C1.85625 14.8271 1.20312 13.8569 0.721875 12.7417C0.240625 11.6264 0 10.4347 0 9.16667C0 7.89861 0.240625 6.70694 0.721875 5.59167C1.20312 4.47639 1.85625 3.50625 2.68125 2.68125C3.50625 1.85625 4.47639 1.20312 5.59167 0.721875C6.70694 0.240625 7.89861 0 9.16667 0C10.4347 0 11.6264 0.240625 12.7417 0.721875C13.8569 1.20312 14.8271 1.85625 15.6521 2.68125C16.4771 3.50625 17.1302 4.47639 17.6115 5.59167C18.0927 6.70694 18.3333 7.89861 18.3333 9.16667C18.3333 10.4347 18.0927 11.6264 17.6115 12.7417C17.1302 13.8569 16.4771 14.8271 15.6521 15.6521C14.8271 16.4771 13.8569 17.1302 12.7417 17.6115C11.6264 18.0927 10.4347 18.3333 9.16667 18.3333ZM9.16667 16.5C11.2139 16.5 12.9479 15.7896 14.3688 14.3688C15.7896 12.9479 16.5 11.2139 16.5 9.16667C16.5 7.11944 15.7896 5.38542 14.3688 3.96458C12.9479 2.54375 11.2139 1.83333 9.16667 1.83333C7.11944 1.83333 5.38542 2.54375 3.96458 3.96458C2.54375 5.38542 1.83333 7.11944 1.83333 9.16667C1.83333 11.2139 2.54375 12.9479 3.96458 14.3688C5.38542 15.7896 7.11944 16.5 9.16667 16.5Z"
                      fill="#10B981"
                    />
                  </svg>
                </span>
                <span className="text-[#374151] font-medium">
                  Completed Orders
                </span>
              </div>
              <span className="text-[16px] font-bold text-[#1A1A1A]">
                {data?.orderCounts?.completed?.toLocaleString() ?? '—'}
              </span>
            </div>
            {/* Orders in Processing  */}
            <div className="flex items-center justify-between py-2.5 bg-[#F8F9FA] mt-2 mb-2">
              <div className="flex items-center gap-2.5">
                <span>
                  <svg
                    width="17"
                    height="18"
                    viewBox="0 0 17 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.2 15.6825V9.5175L1.8 6.39V12.555L7.2 15.6825ZM9 15.6825L14.4 12.555V6.39L9 9.5175V15.6825ZM7.2 17.7525L0.9 14.13C0.615 13.965 0.39375 13.7475 0.23625 13.4775C0.07875 13.2075 0 12.9075 0 12.5775V5.4225C0 5.0925 0.07875 4.7925 0.23625 4.5225C0.39375 4.2525 0.615 4.035 0.9 3.87L7.2 0.2475C7.485 0.0825 7.785 0 8.1 0C8.415 0 8.715 0.0825 9 0.2475L15.3 3.87C15.585 4.035 15.8063 4.2525 15.9638 4.5225C16.1213 4.7925 16.2 5.0925 16.2 5.4225V12.5775C16.2 12.9075 16.1213 13.2075 15.9638 13.4775C15.8063 13.7475 15.585 13.965 15.3 14.13L9 17.7525C8.715 17.9175 8.415 18 8.1 18C7.785 18 7.485 17.9175 7.2 17.7525ZM11.7 5.8725L13.4325 4.8825L8.1 1.8L6.345 2.8125L11.7 5.8725ZM8.1 7.965L9.855 6.9525L4.5225 3.87L2.7675 4.8825L8.1 7.965Z"
                      fill="#F59E0B"
                    />
                  </svg>
                </span>
                <span className="text-[#374151] font-medium">
                  Orders in Processing
                </span>
              </div>
              <span className="text-[16px] font-bold text-[#1A1A1A]">
                {data?.orderCounts?.pending?.toLocaleString() ?? '—'}
              </span>
            </div>
          </div>
        </div>

        {/* LeaderBoard */}
        <div>
          <h2 className="text-lg font-medium text-[#1A1A1A] mt-3 mb-3">
            Leaderboard
          </h2>

          <div className="flex gap-3.5">
            {/* Left card */}
            <div className="flex-1 bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-8 h-8 rounded-lg bg-[#DBEAFE] p-2 flex items-center justify-center">
                  <span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M0 16V8H4V16H0ZM6 16V0H10V16H6ZM12 16V5H16V16H12Z"
                        fill="#3B82F6"
                      />
                    </svg>
                  </span>
                </div>
                <span className="text-sm font-bold text-gray-900">
                  Today&apos;s Summary
                </span>
              </div>
              <div className="flex items-center justify-between py-2.5 bg-[#F8F9FA] mt-2 mb-2">
                <div className="flex items-center gap-2.5">
                  <span>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4.12875 17.4713C3.77625 17.1188 3.6 16.695 3.6 16.2C3.6 15.705 3.77625 15.2813 4.12875 14.9288C4.48125 14.5763 4.905 14.4 5.4 14.4C5.895 14.4 6.31875 14.5763 6.67125 14.9288C7.02375 15.2813 7.2 15.705 7.2 16.2C7.2 16.695 7.02375 17.1188 6.67125 17.4713C6.31875 17.8238 5.895 18 5.4 18C4.905 18 4.48125 17.8238 4.12875 17.4713ZM13.1288 17.4713C12.7763 17.1188 12.6 16.695 12.6 16.2C12.6 15.705 12.7763 15.2813 13.1288 14.9288C13.4813 14.5763 13.905 14.4 14.4 14.4C14.895 14.4 15.3188 14.5763 15.6713 14.9288C16.0238 15.2813 16.2 15.705 16.2 16.2C16.2 16.695 16.0238 17.1188 15.6713 17.4713C15.3188 17.8238 14.895 18 14.4 18C13.905 18 13.4813 17.8238 13.1288 17.4713ZM4.635 3.6L6.795 8.1H13.095L15.57 3.6H4.635ZM3.78 1.8H17.055C17.4 1.8 17.6625 1.95375 17.8425 2.26125C18.0225 2.56875 18.03 2.88 17.865 3.195L14.67 8.955C14.505 9.255 14.2838 9.4875 14.0063 9.6525C13.7288 9.8175 13.425 9.9 13.095 9.9H6.39L5.4 11.7H16.2V13.5H5.4C4.725 13.5 4.215 13.2038 3.87 12.6113C3.525 12.0188 3.51 11.43 3.825 10.845L5.04 8.64L1.8 1.8H0V0H2.925L3.78 1.8Z"
                        fill="#3B82F6"
                      />
                    </svg>
                  </span>
                  <span className="text-[#374151] font-medium">
                    Total Orders
                  </span>
                </div>
                <span className="text-[16px] font-bold text-[#1A1A1A]">
                  {data?.todayOrderCounts?.total?.toLocaleString() ?? '—'}
                </span>
              </div>

              {/* Completed Orders  */}
              <div className="flex items-center justify-between py-2.5 bg-[#F8F9FA] mt-2 mb-2">
                <div className="flex items-center gap-2.5">
                  <span>
                    <svg
                      width="19"
                      height="19"
                      viewBox="0 0 19 19"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7.88333 13.3833L14.3458 6.92083L13.0625 5.6375L7.88333 10.8167L5.27083 8.20417L3.9875 9.4875L7.88333 13.3833ZM9.16667 18.3333C7.89861 18.3333 6.70694 18.0927 5.59167 17.6115C4.47639 17.1302 3.50625 16.4771 2.68125 15.6521C1.85625 14.8271 1.20312 13.8569 0.721875 12.7417C0.240625 11.6264 0 10.4347 0 9.16667C0 7.89861 0.240625 6.70694 0.721875 5.59167C1.20312 4.47639 1.85625 3.50625 2.68125 2.68125C3.50625 1.85625 4.47639 1.20312 5.59167 0.721875C6.70694 0.240625 7.89861 0 9.16667 0C10.4347 0 11.6264 0.240625 12.7417 0.721875C13.8569 1.20312 14.8271 1.85625 15.6521 2.68125C16.4771 3.50625 17.1302 4.47639 17.6115 5.59167C18.0927 6.70694 18.3333 7.89861 18.3333 9.16667C18.3333 10.4347 18.0927 11.6264 17.6115 12.7417C17.1302 13.8569 16.4771 14.8271 15.6521 15.6521C14.8271 16.4771 13.8569 17.1302 12.7417 17.6115C11.6264 18.0927 10.4347 18.3333 9.16667 18.3333ZM9.16667 16.5C11.2139 16.5 12.9479 15.7896 14.3688 14.3688C15.7896 12.9479 16.5 11.2139 16.5 9.16667C16.5 7.11944 15.7896 5.38542 14.3688 3.96458C12.9479 2.54375 11.2139 1.83333 9.16667 1.83333C7.11944 1.83333 5.38542 2.54375 3.96458 3.96458C2.54375 5.38542 1.83333 7.11944 1.83333 9.16667C1.83333 11.2139 2.54375 12.9479 3.96458 14.3688C5.38542 15.7896 7.11944 16.5 9.16667 16.5Z"
                        fill="#10B981"
                      />
                    </svg>
                  </span>
                  <span className="text-[#374151] font-medium">
                    Completed Orders
                  </span>
                </div>
                <span className="text-[16px] font-bold text-[#1A1A1A]">
                  {data?.todayOrderCounts?.completed?.toLocaleString() ?? '—'}
                </span>
              </div>
              {/* Orders in Processing  */}
              <div className="flex items-center justify-between py-2.5 bg-[#F8F9FA] mt-2 mb-2">
                <div className="flex items-center gap-2.5">
                  <span>
                    <svg
                      width="17"
                      height="18"
                      viewBox="0 0 17 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7.2 15.6825V9.5175L1.8 6.39V12.555L7.2 15.6825ZM9 15.6825L14.4 12.555V6.39L9 9.5175V15.6825ZM7.2 17.7525L0.9 14.13C0.615 13.965 0.39375 13.7475 0.23625 13.4775C0.07875 13.2075 0 12.9075 0 12.5775V5.4225C0 5.0925 0.07875 4.7925 0.23625 4.5225C0.39375 4.2525 0.615 4.035 0.9 3.87L7.2 0.2475C7.485 0.0825 7.785 0 8.1 0C8.415 0 8.715 0.0825 9 0.2475L15.3 3.87C15.585 4.035 15.8063 4.2525 15.9638 4.5225C16.1213 4.7925 16.2 5.0925 16.2 5.4225V12.5775C16.2 12.9075 16.1213 13.2075 15.9638 13.4775C15.8063 13.7475 15.585 13.965 15.3 14.13L9 17.7525C8.715 17.9175 8.415 18 8.1 18C7.785 18 7.485 17.9175 7.2 17.7525ZM11.7 5.8725L13.4325 4.8825L8.1 1.8L6.345 2.8125L11.7 5.8725ZM8.1 7.965L9.855 6.9525L4.5225 3.87L2.7675 4.8825L8.1 7.965Z"
                        fill="#F59E0B"
                      />
                    </svg>
                  </span>
                  <span className="text-[#374151] font-medium">
                    Orders in Processing
                  </span>
                </div>
                <span className="text-[16px] font-bold text-[#1A1A1A]">
                  {data?.todayOrderCounts?.pending?.toLocaleString() ?? '—'}
                </span>
              </div>
            </div>
            <div className="flex-1 flex flex-col gap-3.5">
              {/* Animals This Billing Period */}
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <div className="flex items-center gap-2.5 mb-2.5">
                  <span className="p-2 bg-[#EDE9FE] rounded-lg">
                    <svg
                      width="22"
                      height="20"
                      viewBox="0 0 22 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10.5264 8.1582C10.9931 8.15821 11.414 8.26604 11.8027 8.48047C12.2249 8.71337 12.5982 9.01951 12.9238 9.4043L12.9326 9.41504C13.3901 9.93793 13.7947 10.502 14.1465 11.1074C14.5517 11.8047 15.0278 12.4547 15.5732 13.0566L15.5771 13.0615C16.1901 13.727 16.808 14.401 17.4297 15.084C17.9221 15.6251 18.1582 16.2347 18.1582 16.9473C18.1582 17.5835 17.9583 18.1117 17.5537 18.5693C17.1646 19.0095 16.7153 19.2109 16.1582 19.2109C15.255 19.2109 14.3605 19.1348 13.4746 18.9844C12.5009 18.819 11.5178 18.7373 10.5264 18.7373C9.53491 18.7373 8.55181 18.819 7.57812 18.9844C6.69221 19.1348 5.79776 19.2109 4.89453 19.2109C4.33736 19.2109 3.8882 19.0096 3.49902 18.5693C3.09444 18.1117 2.89453 17.5835 2.89453 16.9473C2.89455 16.2347 3.13056 15.6251 3.62305 15.084C4.24481 14.4009 4.86166 13.726 5.47461 13.0605C6.02269 12.474 6.49664 11.8351 6.89453 11.1436C7.23411 10.5533 7.63947 9.99821 8.11133 9.47754L8.12012 9.46777L8.12891 9.45703C8.45793 9.06821 8.83694 8.74954 9.26758 8.49707C9.65382 8.27065 10.0685 8.1582 10.5264 8.1582ZM2.63184 5C3.16481 5.00006 3.5847 5.17553 3.94141 5.53223C4.29811 5.88893 4.47357 6.30883 4.47363 6.8418C4.47363 7.37491 4.29818 7.79557 3.94141 8.15234C3.58474 8.50892 3.16472 8.68451 2.63184 8.68457C2.09873 8.68457 1.67806 8.50911 1.32129 8.15234C0.964537 7.79558 0.789062 7.37489 0.789062 6.8418C0.78913 6.30894 0.964727 5.88888 1.32129 5.53223C1.67806 5.17546 2.09873 5 2.63184 5ZM18.4209 5C18.954 5 19.3747 5.17546 19.7314 5.53223C20.088 5.88887 20.2626 6.30896 20.2627 6.8418C20.2627 7.37487 20.0882 7.79559 19.7314 8.15234C19.3747 8.50911 18.954 8.68457 18.4209 8.68457C17.888 8.68454 17.468 8.50891 17.1113 8.15234C16.7546 7.79557 16.5791 7.37491 16.5791 6.8418C16.5792 6.30883 16.7546 5.88893 17.1113 5.53223C17.468 5.17554 17.8879 5.00003 18.4209 5ZM7.36816 0.789062C7.90127 0.789062 8.32194 0.96452 8.67871 1.32129C9.03548 1.67806 9.21094 2.09873 9.21094 2.63184C9.21088 3.16471 9.03527 3.58474 8.67871 3.94141C8.32194 4.29818 7.90127 4.47363 7.36816 4.47363C6.83518 4.47358 6.41531 4.29812 6.05859 3.94141C5.70188 3.58469 5.52642 3.16482 5.52637 2.63184C5.52637 2.09873 5.70182 1.67806 6.05859 1.32129C6.41526 0.964733 6.83529 0.789119 7.36816 0.789062ZM13.6846 0.789062C14.2174 0.789141 14.6375 0.964721 14.9941 1.32129C15.3509 1.67806 15.5264 2.09873 15.5264 2.63184C15.5263 3.1648 15.3508 3.58471 14.9941 3.94141C14.6375 4.2981 14.2175 4.47355 13.6846 4.47363C13.1515 4.47363 12.7308 4.29818 12.374 3.94141C12.0174 3.58473 11.8419 3.16473 11.8418 2.63184C11.8418 2.09873 12.0173 1.67806 12.374 1.32129C12.7308 0.964554 13.1515 0.789062 13.6846 0.789062Z"
                        stroke="#8B5CF6"
                        strokeWidth="1.57895"
                      />
                    </svg>
                  </span>
                  <span className="text-sm text-[#374151] font-medium">
                    Animals This Billing Period
                  </span>
                </div>
                <div className="flex items-baseline gap-6 mt-3">
                  <span className="text-[28px] font-bold text-[#1A1A1A]">
                    {data?.totalAninamals?.toLocaleString() ?? '—'}
                  </span>
                  <span className="text-sm text-[#6B7280]">Animals</span>
                </div>
              </div>

              {/* Customer Summary */}
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <div className="flex items-center gap-2.5 mb-3">
                  <span className="p-2 bg-[#FEE2E2] rounded-lg">
                    <svg
                      width="19"
                      height="10"
                      viewBox="0 0 19 10"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M2.93145 6.02L4.95145 4L2.93145 1.98L0.911445 4L2.93145 6.02ZM13.7314 5.6L15.7314 2.4L17.7314 5.6H13.7314ZM9.33145 4.8C8.66478 4.8 8.09811 4.56667 7.63145 4.1C7.16478 3.63333 6.93145 3.06667 6.93145 2.4C6.93145 1.72 7.16478 1.15 7.63145 0.69C8.09811 0.23 8.66478 0 9.33145 0C10.0114 0 10.5814 0.23 11.0414 0.69C11.5014 1.15 11.7314 1.72 11.7314 2.4C11.7314 3.06667 11.5014 3.63333 11.0414 4.1C10.5814 4.56667 10.0114 4.8 9.33145 4.8ZM9.33145 1.6C9.10478 1.6 8.91478 1.67667 8.76145 1.83C8.60811 1.98333 8.53145 2.17333 8.53145 2.4C8.53145 2.62667 8.60811 2.81667 8.76145 2.97C8.91478 3.12333 9.10478 3.2 9.33145 3.2C9.55811 3.2 9.74811 3.12333 9.90145 2.97C10.0548 2.81667 10.1314 2.62667 10.1314 2.4C10.1314 2.17333 10.0548 1.98333 9.90145 1.83C9.74811 1.67667 9.55811 1.6 9.33145 1.6ZM-0.268555 9.6V8.34C-0.268555 7.75333 0.028112 7.28333 0.621445 6.93C1.21478 6.57667 1.98478 6.4 2.93145 6.4C3.10478 6.4 3.27145 6.40333 3.43145 6.41C3.59145 6.41667 3.74478 6.43333 3.89145 6.46C3.70478 6.72667 3.56478 7.01333 3.47145 7.32C3.37811 7.62667 3.33145 7.95333 3.33145 8.3V9.6H-0.268555ZM4.53145 9.6V8.3C4.53145 7.43333 4.97478 6.73333 5.86145 6.2C6.74811 5.66667 7.90478 5.4 9.33145 5.4C10.7714 5.4 11.9314 5.66667 12.8114 6.2C13.6914 6.73333 14.1314 7.43333 14.1314 8.3V9.6H4.53145ZM15.7314 6.4C16.6914 6.4 17.4648 6.57667 18.0514 6.93C18.6381 7.28333 18.9314 7.75333 18.9314 8.34V9.6H15.3314V8.3C15.3314 7.95333 15.2881 7.62667 15.2014 7.32C15.1148 7.01333 14.9848 6.72667 14.8114 6.46C14.9581 6.43333 15.1081 6.41667 15.2614 6.41C15.4148 6.40333 15.5714 6.4 15.7314 6.4ZM9.33145 7C8.57145 7 7.89145 7.1 7.29145 7.3C6.69145 7.5 6.33811 7.73333 6.23145 8H12.4514C12.3314 7.73333 11.9748 7.5 11.3814 7.3C10.7881 7.1 10.1048 7 9.33145 7Z"
                        fill="#DC2626"
                      />
                    </svg>
                  </span>
                  <span className="font-bold text-[#1A1A1A]">
                    Customer Summary
                  </span>
                </div>
                <div className="bg-gray-50 rounded-lg px-4 py-3 flex items-baseline gap-3">
                  <span className="text-[28px] font-bold text-[#1A1A1A]">
                    {data?.butcherCustomersCount?.toLocaleString() ?? '—'}
                  </span>
                  <span className="text-[#6C757D]">
                    Total Number of Customers
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
